import * as _ from 'lodash';

import type { Category } from '../../../../../shared/issuesV2';
//  Note that we are intentionally using this Project type (and not the model-typings one), since it includes the associations that we need.
import type { Project, SelectOption } from '../../../../types';

const projectLicensingIssueSettingsFields = [
  'policyId',
  'licensingIssueScanningEnabled',
  'licensingStatusCheckEnabled',
] as const;
export type ProjectLicensingIssueSettings = Pick<Project, typeof projectLicensingIssueSettingsFields[number]>;

const projectSecurityIssueSettingsFields = [
  'securityPolicyId',
  'securityIssueScanningEnabled',
  'securityStatusCheckEnabled',
] as const;
export type ProjectSecurityIssueSettings = Pick<Project, typeof projectSecurityIssueSettingsFields[number]>;

const projectQualityIssueSettingsFields = [
  'qualityPolicyId',
  'qualityIssueScanningEnabled',
  'qualityStatusCheckEnabled',
] as const;
export type ProjectQualityIssueSettings = Pick<Project, typeof projectQualityIssueSettingsFields[number]>;

const projectSBOMPolicySettingsFields = ['sbomPolicyId', 'sbomAnalysisEnabled'] as const;
export type ProjectSBOMPolicySettings = Pick<Project, typeof projectSBOMPolicySettingsFields[number]>;

const projectSnippetLicensingSettingsFields = ['snippetLicensingIssueScanningEnabled'] as const;
export type ProjectSnippetLicensingSettings = Pick<Project, typeof projectSnippetLicensingSettingsFields[number]>;

const projectSnippetSecuritySettingsFields = ['snippetSecurityIssueScanningEnabled'] as const;
export type ProjectSnippetSecuritySettings = Pick<Project, typeof projectSnippetSecuritySettingsFields[number]>;

const projectVendoredLicensingSettingsFields = ['vendoredLicensingIssueScanningEnabled'] as const;
export type ProjectVendoredLicensingSettings = Pick<Project, typeof projectVendoredLicensingSettingsFields[number]>;

const projectVendoredSecuritySettingsFields = ['vendoredSecurityIssueScanningEnabled'] as const;
export type ProjectVendoredSecuritySettings = Pick<Project, typeof projectVendoredSecuritySettingsFields[number]>;

const projectVendoredQualitySettingsFields = ['vendoredQualityIssueScanningEnabled'] as const;
export type ProjectVendoredQualitySettings = Pick<Project, typeof projectVendoredQualitySettingsFields[number]>;

export type ProjectIssueFilters = { filters: Partial<Record<Category, number>> };
export type ProjectIssueSettings =
  & ProjectLicensingIssueSettings
  & ProjectSecurityIssueSettings
  & ProjectQualityIssueSettings
  & ProjectSBOMPolicySettings
  & ProjectSnippetLicensingSettings
  & ProjectSnippetSecuritySettings
  & ProjectVendoredLicensingSettings
  & ProjectVendoredSecuritySettings
  & ProjectVendoredQualitySettings
  & ProjectIssueFilters;

export const pickIssueSettingsFromProject = (project: Project): ProjectIssueSettings =>
  _.pick(project, [
    ...projectLicensingIssueSettingsFields,
    ...projectSecurityIssueSettingsFields,
    ...projectQualityIssueSettingsFields,
    ...projectSBOMPolicySettingsFields,
    ...projectSnippetLicensingSettingsFields,
    ...projectSnippetSecuritySettingsFields,
    ...projectVendoredLicensingSettingsFields,
    ...projectVendoredSecuritySettingsFields,
    ...projectVendoredQualitySettingsFields,
    //  We also want to include the filter IDs for each category.
    'filters',
  ]);

export type GenericIssueSettingsProps = {
  isScanningEnabled: boolean;
  onToggleIsScanningEnabled: (isChecked: boolean) => void;
  policyOptions: Array<SelectOption<number | null>>;
  selectedPolicyOption: SelectOption<number | null>;
  onSelectedPolicyChange: (policyId: number | null) => void;
  isCICheckEnabled: boolean;
  onToggleIsCICheckEnabled: (isChecked: boolean) => void;
  filterOptions: Array<SelectOption<number | null>>;
  selectedFilterOption: SelectOption<number | null>;
  onSelectedFilterChange: (policyId: number | null) => void;
  canEditPolicy: boolean;
  canEditFilters: boolean;
};

type SnippetScanningProps = {
  isSnippetScanningEnabled: boolean;
  onToggleSnippetScanning: (isChecked: boolean) => void;
};

type VendoredScanningProps = {
  isVendoredScanningEnabled: boolean;
  onToggleVendoredScanning: (isChecked: boolean) => void;
};

export type IssueSettingsProps = GenericIssueSettingsProps & SnippetScanningProps & VendoredScanningProps;

export type QualityIssueSettingsProps = GenericIssueSettingsProps & VendoredScanningProps;
